'use client';

import Link from 'next/link';
import { useState } from 'react';

import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/contexts/LocaleContext';
import { Event } from '@/payload-types';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

import DisplayEventDateTime from '../DisplayEventDateTime';
import styles from './EventDetails.module.scss';

type Props = {
  eventDetails: Event['eventDetails'];
  otherLocations: Event['otherLocations'];
  eventData: any;
};

export const EventDetails: React.FC<Props> = ({
  eventData,
  eventDetails,
  otherLocations,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const locale = useLocale();
  const t = getDictionary(locale);

  const startDate =
    (eventDetails?.dates?.startDate &&
      new Date(eventDetails?.dates?.startDate)) ||
    null;
  const endDate =
    (eventDetails?.dates?.endDate && new Date(eventDetails?.dates?.endDate)) ||
    null;

  const handleModalOpen = () => {
    setModalOpen(true);

    // Lock body scroll
    document.body.style.overflow = 'hidden';
  };

  const handleModalClose = () => {
    setModalOpen(false);

    // Unlock body scroll
    document.body.style.overflow = 'auto';
  };

  const handleEventSignupTracking = () => {
    pushGTMEvent({
      event: 'event_signup',
      event_name: eventData.event_name,
      event_type: eventData.event_type,
      event_price: eventData.event_price,
      event_location: eventData.event_location,
      event_duration_hours: eventData.event_duration_hours,
      event_speakers: eventData.event_speakers,
    });
  };

  return (
    <>
      <div
        className={cn(
          styles.details,
          otherLocations && otherLocations.length > 0 && styles.hasLocations
        )}
      >
        {startDate && endDate && (
          <time dateTime={startDate.toISOString()} className={styles.dateTime}>
            <span className={styles.label}>{t('events.dateAndTime')}</span>
            <span className={styles.date}>
              {DisplayEventDateTime(startDate, endDate)}
            </span>
            <span className={styles.time}>
              Kl.{' '}
              {startDate.toLocaleTimeString('da-DK', {
                hour: 'numeric',
                minute: 'numeric',
              })}
              {endDate &&
                `-${endDate.toLocaleTimeString('da-DK', {
                  hour: 'numeric',
                  minute: 'numeric',
                })}`}
            </span>
          </time>
        )}
        {eventDetails.location && (
          <div className={styles.location}>
            <span className={styles.label}>{t('events.location')}</span>
            {eventDetails.location.online ? (
              <span className={styles.online}>{t('events.online')}</span>
            ) : (
              <address className={styles.address}>
                {`${eventDetails.location.address && eventDetails.location.address}, ${
                  eventDetails.location.zipCode && eventDetails.location.zipCode
                } ${eventDetails.location.city && eventDetails.location.city}`}
              </address>
            )}
          </div>
        )}
        {eventDetails.price && (
          <div className={styles.price}>
            <span className={styles.label}>{t('events.price')}</span>
            <span className={styles.amount}>{eventDetails.price.price}</span>
            <span className={styles.priceDescription}>
              {eventDetails.price.priceDescription}
            </span>
            {eventDetails.price.openOffCanvas ? (
              <Button
                className={cn(
                  styles.signupButton,
                  'button button--level-1 button--no-arrow'
                )}
                onClick={handleModalOpen}
              >
                {eventDetails.price.urlLabel
                  ? eventDetails.price.urlLabel
                  : t('events.signup')}
              </Button>
            ) : (
              eventDetails.price.url && (
                <a
                  href={eventDetails.price.url}
                  className={cn(
                    styles.signupButton,
                    'button button--level-1 button--no-arrow'
                  )}
                  onClick={handleEventSignupTracking}
                  target='_blank'
                >
                  {eventDetails.price.urlLabel
                    ? eventDetails.price.urlLabel
                    : t('events.signup')}
                </a>
              )
            )}
          </div>
        )}
      </div>
      {otherLocations &&
        (otherLocations as Event[]).map((location, index) => {
          const label = location.eventDetails?.location?.online
            ? t('events.canAlsoBeExperiencedOnline')
            : location.eventDetails?.location?.city
              ? `${t('events.canAlsoBeExperiencedIn')} ${location.eventDetails.location.city}`
              : '';

          if (!label) {
            return null;
          }

          const startDate =
            (location.eventDetails.dates?.startDate &&
              new Date(location.eventDetails.dates.startDate)) ||
            null;
          const endDate =
            (location.eventDetails.dates?.endDate &&
              new Date(location.eventDetails.dates.endDate)) ||
            null;

          return (
            <div key={index} className={styles.otherLocation}>
              <span className={styles.canAlso}>{label}</span>
              <div className={styles.otherLocationDate}>
                {startDate && endDate && (
                  <span className={styles.otherDate}>
                    {DisplayEventDateTime(startDate, endDate)}
                  </span>
                )}
                {location.eventDetails.location?.online
                  ? t('events.online')
                  : location.eventDetails.location?.city && (
                      <span className={styles.otherCity}>
                        {location.eventDetails.location.city}
                      </span>
                    )}
              </div>
              {location.hero.heading && (
                <span className={styles.otherLocationHeading}>
                  {location.hero.heading}
                </span>
              )}
              <Link
                className='button button--level-4 button--small'
                href={location.url as string}
              >
                {t('events.goToEvent')}
              </Link>
            </div>
          );
        })}
      <Modal
        clickHandler={handleModalClose}
        content={eventDetails.price?.priceOffCanvasContent || {}}
        title={eventDetails.price?.priceOffCanvasTitle || ''}
        modalOpen={modalOpen}
      />
    </>
  );
};
